import React from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { formatTime, getRelativeTime, toTimezone } from '../utils/timezone';
import { useI18n } from '../hooks/useI18n';

interface TimeDisplayProps {
  utcTime: string | Date;
  format?: 'date' | 'time' | 'datetime' | 'relative';
  showTimezone?: boolean;
  className?: string;
}

/**
 * 时间显示组件 - 自动转换为用户时区
 */
const TimeDisplay: React.FC<TimeDisplayProps> = ({
  utcTime,
  format = 'datetime',
  showTimezone = false,
  className = '',
}) => {
  const { settings } = useSettings();
  const { t } = useI18n();

  // 转换时间到用户时区
  const displayTime = React.useMemo(() => {
    if (!utcTime) return '';

    try {
      const targetDate = toTimezone(utcTime, settings.timezone);

      if (format === 'relative') {
        return getRelativeTime(utcTime, settings.timezone, settings.language);
      }

      const formattedTime = formatTime(utcTime, settings.timezone, format, settings.language);

      if (showTimezone) {
        const timezoneName = settings.timezone.split('/').pop()?.replace('_', ' ') || settings.timezone;
        return `${formattedTime} (${timezoneName})`;
      }

      return formattedTime;
    } catch (error) {
      console.error('Time conversion error:', error);
      return String(utcTime);
    }
  }, [utcTime, settings.timezone, settings.language, format, showTimezone]);

  if (!displayTime) {
    return <span className={className}>-</span>;
  }

  return (
    <span
      className={className}
      title={`${t('time.original')}: ${new Date(utcTime).toISOString()}`}
    >
      {displayTime}
    </span>
  );
};

export default TimeDisplay;
